import { useEffect, useRef, useState } from "react";
import { getSpotifyEmbedUrl } from "@/lib/spotify";

interface SpotifyEmbedProps {
  url: string;
  compact?: boolean;
  height?: number;
  className?: string;
  deferUntilVisible?: boolean;
  framed?: boolean;
}

/*
 * Cuanto esperamos como mucho antes de enganchar el observador. El embed de
 * Spotify trae 648 KB de JavaScript de terceros --medido con Lighthouse en la
 * portada, cuatro veces la aplicacion entera-- y con el margen de 1400 px los
 * de debajo montaban de inmediato, compitiendo con la primera pintura: la
 * portada pintaba a 1285 ms y el feed, que no lleva Spotify, a 853. Esperar a
 * que el hilo este libre no cambia lo que ve el visitante --siguen cargados
 * antes de que llegue a ellos-- pero los quita de en medio al entrar.
 */
const ESPERA_MAXIMA_MS = 1500;

/*
 * Ejecuta algo cuando el navegador tenga un hueco. `timeout` obliga a que se
 * ejecute aunque el hilo no se libere nunca. Safari no trae
 * `requestIdleCallback`, de ahi el respaldo.
 */
const cuandoEsteLibre = (fn: () => void): (() => void) => {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(fn, { timeout: ESPERA_MAXIMA_MS });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(fn, ESPERA_MAXIMA_MS);
  return () => window.clearTimeout(id);
};

const SpotifyEmbed = ({
  url,
  compact = false,
  height: heightProp,
  className,
  deferUntilVisible = false,
  framed = true,
}: SpotifyEmbedProps) => {
  const embedUrl = getSpotifyEmbedUrl(url);
  const height = heightProp ?? (compact ? 152 : 352);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldMount, setShouldMount] = useState(!deferUntilVisible);

  useEffect(() => {
    if (!deferUntilVisible) {
      setShouldMount(true);
      return;
    }

    const node = containerRef.current;
    if (!node) return;

    let observer: IntersectionObserver | null = null;
    const cancela = cuandoEsteLibre(() => {
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          setShouldMount(true);
          observer?.disconnect();
        },
        { rootMargin: "1400px 0px" },
      );
      observer.observe(node);
    });

    return () => {
      cancela();
      observer?.disconnect();
    };
  }, [deferUntilVisible]);

  if (!embedUrl) return null;

  const frameCls = framed
    ? "overflow-hidden rounded-xl border border-border/50 bg-muted/20 shadow-sm"
    : "overflow-hidden";

  return (
    <div
      ref={containerRef}
      className={`w-full min-w-0 ${frameCls} ${className ?? ""}`}
      style={{ minHeight: `${height}px` }}
    >
      {shouldMount ? (
        <iframe
          src={embedUrl}
          width="100%"
          height={String(height)}
          style={{ minHeight: `${height}px` }}
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          className={`block w-full ${framed ? "rounded-lg" : ""}`}
          title="Spotify"
        />
      ) : (
        <div className="flex h-full min-h-inherit items-center justify-center bg-muted/30">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-pulse rounded-full bg-foreground/10" />
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Cargando Spotify...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpotifyEmbed;
